import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  provideAppInitializer,
} from '@angular/core';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { BrowserModule } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import { OKTA_CONFIG, OktaAuthModule, OktaCallbackComponent, OktaConfig } from '@okta/okta-angular';
import { OktaAuth, OktaAuthOptions } from '@okta/okta-auth-js';

import { AppRoutingModule } from './app-routing.module';
import { Config } from './core/config/config';
import { provideConfig } from './core/config/config.provider';
import { CONFIG_REPOSITORY } from './core/config/config.repository.token';
import { CONFIG } from './core/config/config.token';
import { CoreModule } from './core/core.module';
import { LoaderInterceptor } from './core/interceptors/loader-interceptor.interceptor';
import { AccessErrorComponent } from './shared/composants/errors/access-error/access-error.component';
import { SharedModule } from './shared/shared.module';
import { CustomPaginator } from './shared/utils/CustomPaginatorConfiguration';
import { DateUtils } from './shared/utils/date-utils';
import { OverlayModule } from '@angular/cdk/overlay';
import { provideNgxMask } from 'ngx-mask';
import { tokenInterceptor } from './core/interceptors/token.interceptor';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { OKTA_MS_AUTH, OKTA_OAS_AUTH } from './core/auth/auth-factory.service';
import { registerIcons } from './core/config/register-icon';

let config: Config;

// Construction de la config Okta
const buildOktaConfig = (cfg: Config): OktaConfig => ({
  oktaAuth: new OktaAuth({
    issuer: cfg.okta.issuer,
    clientId: cfg.okta.clientId,
    redirectUri: cfg.okta.redirectUri,
    scopes: ['openid', 'profile', 'email', 'required_permissions', 'offline_access'],
    pkce: true,
  } as OktaAuthOptions),
});
const buildOktaOasConfig = (config: Config): OktaConfig => ({
  oktaAuth: new OktaAuth({
    issuer: config.okta.oasIssuer,
    clientId: config.okta.clientId,
    redirectUri: config.okta.redirectUri,
    scopes: ['offline_access'],
    pkce: true,
    tokenManager: {
      storage: 'localStorage',
      storageKey: 'okta-oas-token-storage',
    },
  } as OktaAuthOptions),
});
const buildOktaMsConfig = (config: Config): OktaConfig => ({
  oktaAuth: new OktaAuth({
    issuer: config.okta.msIssuer,
    clientId: config.okta.clientId,
    redirectUri: config.okta.redirectUri,
    scopes: ['offline_access'],
    pkce: true,
    tokenManager: {
      storage: 'localStorage',
      storageKey: 'okta-ms-token-storage',
    },
  } as OktaAuthOptions),
});

// Initialise la config avant tout
const initializeConfig = (): Promise<void> => {
  const configRepository = inject(CONFIG_REPOSITORY);
  return configRepository.getConfig().then((conf) => {
    config = conf;
  });
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter([
      { path: 'callback', component: OktaCallbackComponent },
      { path: 'access-error', component: AccessErrorComponent },
    ]),

    provideAppInitializer(initializeConfig),
    provideAppInitializer(registerIcons),
    provideConfig(),
    { provide: CONFIG, useFactory: () => config },

    // HTTP client
    provideHttpClient(withFetch(), withInterceptors([tokenInterceptor])),

    // ✅ On crée OktaAuth directement après chargement du config
    {
      provide: OKTA_CONFIG,
      useFactory: () => buildOktaConfig(config),
    },
    importProvidersFrom(OktaAuthModule),
    {
      provide: OKTA_OAS_AUTH,
      useFactory: (config: Config) => buildOktaOasConfig(config).oktaAuth,
      deps: [CONFIG],
    },
    importProvidersFrom(OktaAuthModule),
    {
      provide: OKTA_MS_AUTH,
      useFactory: (config: Config) => buildOktaMsConfig(config).oktaAuth,
      deps: [CONFIG],
    },

    // Import des modules existants
    importProvidersFrom(
      CoreModule,
      SharedModule,
      BrowserModule,
      AppRoutingModule,
      OverlayModule,
      OktaAuthModule,
    ),

    provideAnimationsAsync(),
    provideNgxMask(),
    { provide: LOCALE_ID, useValue: 'fr-FR' },
    { provide: MAT_DATE_LOCALE, useValue: 'fr-FR' },
    { provide: MAT_DATE_FORMATS, useValue: DateUtils.MY_DATE_FORMATS },
    { provide: MatPaginatorIntl, useValue: CustomPaginator() },

    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },

    // Material Fields
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic',
      },
    },
  ],
};
